import React, { useLayoutEffect, useRef, useState } from 'react';
import { Typography, TypographyProps, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@eposnow/ui-core';

interface GenericContent {
  fieldKey?: string;
  text?: string;
  hasNonTextContent?: boolean;
  nonTextContent?: React.ReactNode;
  shouldTranslate: boolean;
  defaultText?: string;
  shouldTranslateDefault?: boolean;
  shouldClipContent?: boolean;
  typographyProps?: TypographyProps;
}

const GenericGridContent = ({ content }: { content: GenericContent[] }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const ref = useRef(null);
  const [overflowed, setOverflowed] = useState(false);

  useLayoutEffect(() => {
    if (ref?.current) {
      setOverflowed(ref.current.scrollWidth > ref.current.clientWidth);
    }
  }, [ref]);

  return (
    <>
      {content.map((entry) => {
        const text = entry.text ? entry.text : entry.defaultText;
        const shouldTranslate = entry.text ? entry.shouldTranslate : entry.shouldTranslateDefault;

        if (!text && !entry?.hasNonTextContent) return null;

        if (entry?.hasNonTextContent) return entry?.nonTextContent;

        const translatedText = shouldTranslate ? t(text) : text;

        const textReturn = (
          <Typography
            ref={ref}
            key={entry.fieldKey ? `${entry.fieldKey}-${translatedText}` : translatedText}
            {...entry.typographyProps}
            sx={{
              lineHeight: '1.2',
              ...(!entry.shouldClipContent && {
                whiteSpace: 'break-spaces !important',
              }),
              ...(entry.shouldClipContent && {
                maxHeight: '24px',
                whiteSpace: 'nowrap !important',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
              }),
            }}
          >
            {translatedText}
          </Typography>
        );

        return overflowed && entry.shouldClipContent ? (
          <Tooltip
            key={entry.fieldKey ? `${entry.fieldKey}-${translatedText}` : translatedText}
            title={entry.shouldClipContent ? translatedText : ''}
            theme={theme}
          >
            {textReturn}
          </Tooltip>
        ) : (
          textReturn
        );
      })}
    </>
  );
};

export default GenericGridContent;
